import React from 'react';
import { graphql, useFragment } from 'react-relay';

import {
  BrandMessageTaskDescriptionList_brandMessageTask$data,
  BrandMessageTaskDescriptionList_brandMessageTask$key,
} from '../../../relay/__generated__/BrandMessageTaskDescriptionList_brandMessageTask.graphql';
import DateText from '../../core/DateText';
import DescriptionList, { DescriptionListProps } from '../../core/DescriptionList';
import Grid from '../../core/Grid';
import Image from '../../core/Image';
import Label from '../../core/Label';
import Link from '../../core/Link';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import View from '../../core/View';
import UserAvatarText from '../../user/UserAvatarText';

const BrandMessageTaskDescriptionList_brandMessageTask = graphql`
  fragment BrandMessageTaskDescriptionList_brandMessageTask on BrandMessageTask {
    id

    description
    period
    query
    startAt

    created
    createdBy {
      ...UserAvatarText_user
    }

    isActive

    template {
      id
      templateCode
      templateName
      createTime
      serviceStatus
      updateTime

      templateCode
      parameterNames

      imageLink
      imageUrl
      content

      buttons {
        name
        linkMobile
        linkType
        linkIos
      }
    }
  }
`;

type Props = {
  brandMessageTask: BrandMessageTaskDescriptionList_brandMessageTask$key;
  type?: 'default' | 'activity' | 'template';
} & Omit<
  DescriptionListProps<BrandMessageTaskDescriptionList_brandMessageTask$data>,
  'item' | 'itemDescriptions' | 'picks'
>;
const BrandMessageTaskDescriptionList = ({
  brandMessageTask: brandMessageTaskReference,
  type = 'default',
  ...props
}: Props) => {
  const brandMessageTask = useFragment(BrandMessageTaskDescriptionList_brandMessageTask, brandMessageTaskReference);

  return (
    <DescriptionList
      item={brandMessageTask}
      itemDescriptions={{
        'isActive': {
          title: '상태',
          renderValue: ({ isActive }) =>
            isActive ? <Label variant={'success'}>활성화</Label> : <Label variant={'secondary'}>비활성화</Label>,
        },
        'created': {
          title: '제작일',
          renderValue: ({ created }) => <DateText fontSize={1}>{created}</DateText>,
        },
        'createdBy': {
          title: '제작자',
          renderValue: ({ createdBy }) => <UserAvatarText user={createdBy} />,
        },

        'description': {
          title: '설명',
          renderValue: ({ description }) =>
            description ? (
              <Text fontSize={1} whiteSpace={'pre-wrap'}>
                {description}
              </Text>
            ) : undefined,
        },
        'startAt': {
          title: '시작일',
          renderValue: ({ startAt }) => <DateText fontSize={1}>{startAt}</DateText>,
        },
        'period': {
          title: '주기',
          renderValue: ({ period }) => (period ? <Text fontSize={1}>{period}일</Text> : undefined),
        },

        'template.parameterNames': {
          title: '변수명',
          renderValue: ({ template: { parameterNames } }) => (
            <Stack gapX={1} gapY={1} wrap={true}>
              {parameterNames.map((name) => (
                <Stack.Item key={name}>
                  <Label variant={'default'}>{name}</Label>
                </Stack.Item>
              ))}
            </Stack>
          ),
        },
        'template.image': {
          title: '이미지',
          renderValue: ({ template: { imageLink, imageUrl } }) => {
            const imageComponent = <Image html src={imageUrl || ''} alt={'브랜드 메세지 이미지'} />;
            return (
              <View>
                <View maxWidth={300} lineHeight={0}>
                  {imageLink ? (
                    <Link target={'_blank'} href={imageLink}>
                      {imageComponent}
                    </Link>
                  ) : (
                    imageComponent
                  )}
                </View>
                <Text fontSize={0} color={'fg.muted'} sx={{ wordBreak: 'break-all' }}>
                  {imageLink ? `(${imageLink})` : '-'}
                </Text>
              </View>
            );
          },
        },
        'template.content': {
          title: '본문',
          renderValue: ({ template: { content } }) => (
            <Text fontSize={1} whiteSpace={'pre-wrap'}>
              {content}
            </Text>
          ),
        },
        'template.buttons': {
          title: '버튼',
          renderValue: ({ template: { buttons: _buttons } }) => {
            const buttons = _buttons ?? [];

            if (buttons.length === 0) return undefined;
            else
              return buttons.map(({ name, linkMobile, linkType, linkIos }, i) => (
                <View key={name + linkMobile} mt={i === 0 ? 0 : 2}>
                  <Grid gapX={1} wrap={[true, false, false]} sx={{ alignItems: 'baseline' }}>
                    <Grid.Unit size={'min'}>
                      <View width={185}>
                        <Text fontSize={1}>{name}</Text>
                      </View>
                    </Grid.Unit>
                    <Grid.Unit size={'max'}>
                      <Text fontSize={0} color={'fg.muted'} sx={{ wordBreak: 'break-all' }}>
                        ({linkType === 'AL' ? linkIos : linkMobile})
                      </Text>
                    </Grid.Unit>
                  </Grid>
                </View>
              ));
          },
        },
      }}
      picks={
        type === 'default'
          ? ['startAt', 'period', 'description']
          : type === 'activity'
            ? ['isActive', 'created', 'createdBy']
            : ['template.image', 'template.parameterNames', 'template.content', 'template.buttons']
      }
      {...props}
    />
  );
};

export default BrandMessageTaskDescriptionList;
